import React, { FC } from 'react';
import classNames from 'classnames';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { HeaderText } from '@/components/ui/HeaderText';
import {
  HeaderInterface,
  HeaderSize,
} from '@/components/ui/HeaderText/HeaderText';
import styles from './GenBanner.module.scss';
import { GenExternalLink } from '@/components';
import { GenExternalLinkProps } from '@/components/generics/GenExternalLink/GenExternalLink';

export enum BannerColorScheme {
  SUCCESS = 'success',
  NOTICE = 'notice',
  ERROR = 'error',
  HOLIDAY = 'holiday',
  UPSELL = 'upsell',
}

export enum BannerBorderType {
  NONE = 'none',
  SOLID = 'solid',
  DASHED = 'dashed',
}

export enum BannerParagraphAlignment {
  LEFT = 'text-left',
  CENTER = 'text-center',
}

const getBannerStyles = (
  borderType: BannerBorderType,
  colorScheme: BannerColorScheme,
) => {
  const borderStyle =
    borderType === BannerBorderType.DASHED ? styles[`${colorScheme}Dash`] : '';

  const baseStyles = {
    success: 'bg-kelly-green-tint border-success fill-success',
    notice: 'bg-yellow-tint border-yellow',
    error: 'bg-red-tint border-warning',
    holiday: 'bg-yellow-pastel border-red-v2',
    upsell: 'bg-indigo-pastel border-blue-deep',
  };

  return `${baseStyles[colorScheme]} ${borderStyle}`;
};

export const ICON_STYLES = {
  success: '#19883E',
  notice: '#424140',
  error: '#D52300',
  holiday: '#360019',
  upsell: '#424140',
};

const HEADING_STYLES = {
  success: '!text-success',
  notice: '',
  error: '!text-warning',
  holiday: '!text-maroon-dark',
  upsell: '',
};

const PARAGRAPH_STYLES = {
  success: '',
  notice: '',
  error: 'text-warning',
  holiday: 'text-maroon-dark',
  upsell: '',
};

export const ICON_DESKTOP_SIZE_MAPPING: Record<HeaderSize, number> = {
  [HeaderSize.H1]: 42,
  [HeaderSize.H2]: 32,
  [HeaderSize.H3]: 24,
  [HeaderSize.H4]: 18,
  [HeaderSize.HERO]: 64,
};

export const ICON_MOBILE_SIZE_MAPPING: Record<HeaderSize, number> = {
  [HeaderSize.H1]: 32,
  [HeaderSize.H2]: 24,
  [HeaderSize.H3]: 20,
  [HeaderSize.H4]: 18,
  [HeaderSize.HERO]: 64,
};

export interface GenBannerProps {
  colorScheme?: BannerColorScheme;
  borderType?: BannerBorderType;
  ctaAttr?: GenExternalLinkProps;
  headingAttr?: HeaderInterface;
  icon?: React.ReactNode;
  image?: React.ReactNode;
  paragraphText?: React.ReactNode;
  paragraphAlignment?: BannerParagraphAlignment;
  testId?: string;
  classes?: string;
}

const GenBanner: FC<GenBannerProps> = ({
  ctaAttr,
  headingAttr,
  icon,
  image,
  paragraphText,
  colorScheme = BannerColorScheme.UPSELL,
  borderType = BannerBorderType.NONE,
  paragraphAlignment = BannerParagraphAlignment.LEFT,
  testId = 'banner',
  classes = '',
}) => {
  return (
    <div
      data-testid={testId}
      className={classNames(
        'w-full flex flex-col md:flex-row p-4 md:p-6 border rounded-md content-center justify-center items-center',
        getBannerStyles(borderType, colorScheme),
        classes,
      )}
    >
      {image && <div className="self-center md:pr-6 md:pb-0 pb-4">{image}</div>}
      <div className="flex flex-row gap-2 md:gap-4 items-center">
        <div className="items-center flex flex-row md:pr-6">
          {icon && (
            <div
              className={classNames({
                ['self-start']: !!paragraphText,
              })}
            >
              {icon}
            </div>
          )}
          <div className="md:pl-4">
            {headingAttr && (
              <HeaderText
                classes={`${HEADING_STYLES[colorScheme]} !leading-none`}
                {...headingAttr}
              />
            )}
            {paragraphText && (
              <p
                className={`text-sm md:text-base ${PARAGRAPH_STYLES[colorScheme]} ${paragraphAlignment}`}
              >
                {paragraphText}
              </p>
            )}
          </div>
        </div>
      </div>
      {ctaAttr && (
        <GenExternalLink
          {...ctaAttr}
          classes={classNames({
            ['mt-4 md:mt-0']: ctaAttr.style === BUTTON_STYLES.link,
            ['w-full md:w-fit mt-4 md:mt-0']:
              ctaAttr.style === BUTTON_STYLES.primary,
          })}
        />
      )}
    </div>
  );
};

export default React.memo(GenBanner);
